import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import { CircularLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { OtherCompetitorsCell } from "app/pages/benchmark-tracker/components/common/OtherCompetitorsCell";
import { generateColumns } from "app/pages/benchmark-tracker/components/Report/MetricElements/TableView/config";
import TableView from "app/pages/benchmark-tracker/components/Report/MetricElements/TableView/TableView";
import { MetricWrapper } from "app/pages/benchmark-tracker/components/Report/MetricElements/TrafficShare/styles";
import { ColorDomainCellWithLink } from "app/pages/benchmark-tracker/components/Share/ColorDomainCellWithLink/ColorDomainCellWithLink";
import { PieChart } from "app/pages/benchmark-tracker/components/Share/PieChart/PieChart";
import { useTableHover } from "app/pages/benchmark-tracker/hooks/useTableHover";
import { useTrafficShareTableData } from "app/common/hooks/benchmark";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import type { TrafficShareMetricSchema } from "app/pages/benchmark-tracker/types/api.types";
import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { Header } from "./Header";
import { ChartContainer } from "./styles";

type CompetitorTrafficShareSchema = {
    tooltip: string;
    trafficShareData: TrafficShareMetricSchema;
    onOpenModal: () => void;
    isLoading: boolean;
};

export const CompetitorTrafficShare = ({
    trafficShareData,
    tooltip,
    onOpenModal,
    isLoading,
}: CompetitorTrafficShareSchema) => {
    const { granularity } = proNavigator.getParams();
    const { primaryDomains } = useSelector(selectSelectedBenchmark) ?? {};
    const primaryDomain = primaryDomains?.[0];
    const popColumn = granularity === BenchmarkGranularity.MONTHLY ? "mom" : "wow";

    const tableData = useTrafficShareTableData({ primary: primaryDomain ?? "", trafficShareData });
    const { activeRowIndex, onLeave, onRowHover, selectedRow, setSelectedRow } = useTableHover(tableData);

    const onChartPointHover = useCallback(
        (index: number) => {
            setSelectedRow(index);
        },
        [setSelectedRow],
    );

    const tableColumns = useMemo(
        () =>
            generateColumns(["colorDomain", "trafficShare", popColumn, "yoy"], {
                colorDomain: {
                    width: 225,
                    metricName: BenchmarkMetricName.trafficShare,
                    render: (value: string, row: Record<PropertyKey, any>) => {
                        if (row?.isOtherCompetitors) {
                            return <OtherCompetitorsCell onClick={onOpenModal}>{value}</OtherCompetitorsCell>;
                        }
                        return (
                            <ColorDomainCellWithLink
                                metricName={BenchmarkMetricName.trafficShare}
                                value={value}
                                row={row}
                            />
                        );
                    },
                },
                trafficShare: { width: 90 },
                [popColumn]: { width: 90 },
                yoy: { width: 90 },
            }),
        [popColumn, onOpenModal],
    );

    return (
        <MetricWrapper>
            <Header
                tooltip={tooltip}
                onOpenModal={onOpenModal}
                isLoading={isLoading}
            />

            <Flex alignItems="center">
                <ChartContainer>
                    {isLoading ? (
                        <Flex
                            alignItems="center"
                            height="232px"
                        >
                            <CircularLoader isLarge />
                        </Flex>
                    ) : (
                        <PieChart
                            primary={primaryDomain ?? ""}
                            activeIndex={selectedRow}
                            trafficShareData={trafficShareData}
                            onPointHover={onChartPointHover}
                            onLeave={onLeave}
                        />
                    )}
                </ChartContainer>

                <TableView
                    tableData={tableData}
                    columnsConfig={tableColumns}
                    activeRowIndex={activeRowIndex}
                    onRowHover={onRowHover}
                    onMouseTableLeave={onLeave}
                    isLoading={isLoading}
                />
            </Flex>
        </MetricWrapper>
    );
};
